/**
 * Acción "Justificar" bajo el estado de un día (008 · T111; contracts/ui.md §2.2, spec US7-1 y
 * US7-5).
 *
 * Aparece **solo** si el servidor la permite (`justify.kinds` no vacío) y la empresa no está
 * suspendida; el plazo, el tope y la justificación abierta los decide la API (si no se puede, la
 * lista llega vacía y no hay nada que pintar). Un tipo ⇒ va directo a `/justificar/$day?tipo=…`;
 * dos ⇒ bottom sheet para elegir. Sin red queda deshabilitada. Enlace 16 px/600 Coral 700 con
 * objetivo táctil ≥ 44 px.
 */
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import type { JustificationKind, StaffDay } from '@fermin/contracts';

import { cn } from '../../components/ui/cn';
import { t } from '../../i18n';

import { KindSheet } from './kind-sheet';

export interface JustifyActionProps {
  day: StaffDay;
  /** Empresa suspendida: sin acción. */
  readOnly?: boolean;
  online?: boolean;
}

export function JustifyAction({ day, readOnly = false, online = true }: JustifyActionProps) {
  const navigate = useNavigate();
  const [choosing, setChoosing] = useState(false);
  const kinds = day.justify.kinds;
  if (readOnly || kinds.length === 0) return null;

  const go = (kind: JustificationKind) => {
    setChoosing(false);
    void navigate({ to: '/justificar/$day', params: { day: day.day }, search: { tipo: kind } });
  };

  return (
    <>
      <button
        type="button"
        data-testid="day-justify"
        data-kinds={kinds.join(',')}
        disabled={!online}
        onClick={() => (kinds.length === 1 ? go(kinds[0]!) : setChoosing(true))}
        className={cn(
          'inline-flex min-h-11 w-fit items-center self-start rounded-fm-md font-ui text-fm-base font-semibold',
          'text-fm-primary-text underline-offset-2 hover:underline',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fm-focus',
          'disabled:cursor-not-allowed disabled:text-fm-text-secondary disabled:no-underline',
        )}
      >
        {t('justification.action.justify')}
      </button>
      {kinds.length > 1 ? (
        <KindSheet open={choosing} day={day} onChoose={go} onClose={() => setChoosing(false)} />
      ) : null}
    </>
  );
}
